import React from 'react';
import { Link } from '@inertiajs/react';
import { Seo } from '~/marketing/Seo';
import { Card, Button, Statistic } from '~/marketing/ui';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { AEO_DATA, quoted as Q } from '~/marketing/marketingData';
import { PostLink, BENCHMARK, GraderCta } from '~/marketing/Post';
import { benchmark, by, listingShare, ownShare, one, word, and } from '~/marketing/research/figures';
import industries from '~/marketing/industries.json';
import { findingFor } from '~/marketing/industryFindings';

/* One page per industry the benchmark measured, 2026-10-14, replacing the
   hand-written /hvac and /plumbing. Those listed six invented questions under
   "The exact questions we ask", said "one HVAC company gets named" where a
   typical answer named 5.8, and promised to add a customer's own phrasing to a
   question set the product does not edit.

   **Almost everything here is data.** The questions are the industry's real
   twenty, sent as props from `verticals.yml` (see `PagesController
   .industry_props`); every figure is the industry's own, from `benchmark.json`;
   the headline and the nouns are its entry in `industries.json`. The sentences
   around them are one template, approved once, so no industry's page can say
   something the others' cannot. Approved in `docs/industry-pages/drafts.md`. */
const band = { background: 'var(--bg-deep)', borderBlock: '1px solid var(--border-subtle)' };
const secGrid = { paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-8)' };
const intro = { display: 'grid', gap: 'var(--space-3)', maxWidth: '60ch' };
const body = { color: 'var(--text-body)', maxWidth: '62ch', margin: 0 };
const small = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)', margin: 0 };
const pct = n => `${Math.round(n)}%`;
const inCity = q => q.replaceAll('{{city}}', '[your city]');
// "an HVAC company": the acronym is said letter by letter.
const an = noun => (/^(?:[aeiou]|hvac)/i.test(noun) ? 'an' : 'a');

function Questions({ title, items }) {
  return (
    <Card variant="inset">
      <div style={{ display: 'grid', gap: 12 }}>
        <h3 style={{ fontSize: 'var(--fs-h4)' }}>{title} <span className="sp-mono" style={{ fontSize: 13, color: 'var(--text-muted)' }}>({items.length})</span></h3>
        {items.map((q, i) => (
          <div key={q} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <span className="sp-mono" style={{ fontSize: 11, color: 'var(--text-muted)', width: 22, flex: 'none' }}>{String(i + 1).padStart(2, '0')}</span>
            <span className="sp-mono" style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>“{inCity(q)}”</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Industry({ industry, questions }) {
  const page = industries.find(p => p.key === industry);
  const b = by[industry];
  const total = questions.hire.length + questions.choose.length;
  const others = industries.filter(p => p.key !== industry);

  const steps = [
    { h: 'Be listed where AI assistants look.', to: '/blog/get-listed-where-ai-assistants-look',
      b: `For ${page.businesses}, ${pct(listingShare(b))} of the sources cited were places a business can be listed, reviewed or mentioned.` },
    { h: 'Publish pages that answer your customers’ questions.',
      b: `For ${page.businesses}, ${pct(ownShare(b))} of the sources cited were businesses’ own websites.` },
    { h: 'Use one name everywhere.',
      b: 'AI assistants write a business’s name in many ways, and a consistent name is easier to recognize.' },
    { h: 'Measure across repeated answers.', to: '/blog/how-to-measure-ai-visibility',
      b: 'The answer changes each time the question is asked, so a reliable reading takes many answers.' },
  ];

  return (
    <>
      <Seo
        title={`${page.name} · AI search visibility`}
        description={`Which ${page.businesses} AI assistants recommend in your city: who gets named, how much that changes between answers, and which sources they cite.`}
        path={page.path}
      />
      <header className="sp-grid-bg" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="sp-page" style={{ paddingBlock: 'clamp(48px,6vw,84px)' }}>
          <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: 700 }}>
          <span className="sp-label">{page.name}</span>
          <h1 style={{ fontSize: 'var(--fs-display-2)', lineHeight: 'var(--lh-display)' }}>{page.headline}</h1>
          <p style={{ fontSize: 'var(--fs-body-lg)', color: 'var(--text-body)', maxWidth: '56ch', margin: 0 }}>
            We ask {and(AEO_DATA.engines)} the questions your customers ask, in your city, and show you whether your business is named, who else is named, and which sources the answers drew on.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center', marginTop: 'var(--space-2)' }}>
            <Button size="lg" href={routes.grader}>Check your visibility</Button>
            <span className="sp-caption">One question, {word(AEO_DATA.engines.length)} AI assistants, the report emailed to you. No credit card.</span>
          </div>
          </div>
        </div>
      </header>

      <section className="sp-page" style={secGrid}>
        <div style={intro}>
          <span className="sp-label">What we found</span>
          <h2 style={{ fontSize: 'var(--fs-h1)' }}>What AI assistants said when asked about {page.businesses}</h2>
          {/* The same line as this industry's home page card, so the page behind
              a card always says what the card said. */}
          <p style={{ color: 'var(--text-strong)', fontSize: 'var(--fs-body-lg)', margin: 0 }}>{findingFor(industry)}</p>
        </div>
        <Card variant="data">
          <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
            {/* Each industry's own verb, the one its headline uses: nobody hires a
                dentist. The list below carries the same title. */}
            <Statistic label={`Answers to the “${page.ask}” questions that named ${an(page.business)} ${page.business}`} value={b.named.count} of={b.named.of} noun="answers" size="lg" />
            <ul style={{ ...body, paddingLeft: '1.25em', display: 'grid', gap: 'var(--space-2)' }}>
              <li>On average, an answer named {one(b.named_per_answer)} businesses.</li>
              <li>Ask the same question twice and the list changes: of all the businesses named in the two answers, {b.repeat_overlap}% appeared in both.</li>
              <li>In {and(b.cities.map(c => c.city))}, the three most-named {page.businesses} took {and(b.cities.map(c => pct(c.top3_by_brand)))} of all mentions.</li>
              <li>{pct(listingShare(b))} of the sources cited were places a business can be listed, reviewed or mentioned, and {pct(ownShare(b))} were businesses’ own websites.</li>
            </ul>
            <p style={small}>
              Collected on <span className="sp-mono">{b.measured_on}</span>. {Q.benchmark_scope}
              {/* Only the ten the post covers are in it; med spa, PI law and dental were added after. */}
              {benchmark.post.includes(industry) && <> The full results are in <PostLink to={BENCHMARK} />.</>}
            </p>
          </div>
        </Card>
      </section>

      <section style={band}>
        <div className="sp-page" style={secGrid}>
          <div style={intro}>
            <span className="sp-label">The questions we ask</span>
            <h2 style={{ fontSize: 'var(--fs-h1)' }}>The {word(total)} questions every {page.business} is measured on</h2>
            <p style={body}>{Q.questions_basis(total, questions.hire.length)}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(380px,100%),1fr))', gap: 'var(--space-4)', alignItems: 'start' }}>
            <Questions title={page.ask} items={questions.hire} />
            <Questions title="How to choose, and what it costs" items={questions.choose} />
          </div>
        </div>
      </section>

      <section className="sp-page" style={secGrid}>
        <h2 style={{ fontSize: 'var(--fs-h1)' }}>Where to start</h2>
        {/* Four steps: two by two or four across, never three and an orphan. */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.h} style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
              <span className="sp-label">{String(i + 1).padStart(2, '0')}</span>
              <h3 style={{ fontSize: 'var(--fs-h4)' }}>{s.to ? <PostLink to={s.to}>{s.h}</PostLink> : s.h}</h3>
              <p style={small}>{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sp-page" style={{ paddingBottom: 'var(--section-y)', display: 'grid', gap: 'var(--space-8)' }}>
        <GraderCta heading={`See which ${page.businesses} AI assistants name in your city.`} />
        <p className="sp-caption" style={{ margin: 0 }}>
          Other industries:{' '}
          {others.map((p, i) => (
            <React.Fragment key={p.key}>
              {i > 0 && ' · '}
              <Link href={p.path}>{p.name}</Link>
            </React.Fragment>
          ))}
        </p>
      </section>
    </>
  );
}

Industry.layout = withMarketingLayout;
